import axios from 'axios';
import React from 'react';
import { useUserStore } from '@/stores/user/userStore';
import { NOTIFICATION_MESSAGES, STATUS } from '@/constants';
import useTeamManagementStore from '@/stores/teamManagement/teamManagementStore';
import toast from 'react-hot-toast';
import useToastStore from '@/stores/toast/toastStore';

// Utility function to replicate useToast addErrorToast functionality
// This can be used outside React components (like in axios interceptors)
const addErrorToast = (toastData) => {
  // Add to toast store (same as useToast hook)
  const addToast = useToastStore.getState().addToast;
  addToast({ ...toastData, type: STATUS.ERROR });

  // Ensure animation styles are available
  const ensureAnimationStyle = () => {
    if (!document.getElementById('toast-animation-style')) {
      const style = document.createElement('style');
      style.id = 'toast-animation-style';
      style.textContent = `
        @keyframes shrinkToastBar {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }

        .toast-progress-indicator {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: rgba(var(--color-purple-300), 1);
          width: 100%;
          transform-origin: left;
        }
      `;
      document.head.appendChild(style);
    }
  };

  ensureAnimationStyle();

  // Toast options (same as useToast hook)
  const toastOptions = {
    duration: 4000,
    position: 'top-center',
    className: 'toast-with-indicator',
    style: {
      background: 'rgba(var(--color-white), 1)',
      color: 'rgba(var(--color-dark-900), 0.9)',
      padding: '10px 16px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      maxWidth: '320px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid rgba(var(--color-purple-300), 0.2)',
    },
    icon: null
  };

  // Create custom toast (same as useToast hook)
  toast.custom((t) =>
    React.createElement('div', {
      className: `${t.visible ? 'animate-enter' : 'animate-leave'} toast-with-indicator`,
      style: {
        ...toastOptions.style,
        opacity: t.visible ? 1 : 0,
      }
    }, [
      toastData.message,
      React.createElement('div', {
        key: 'progress-indicator',
        className: 'toast-progress-indicator',
        style: {
          animation: `shrinkToastBar ${toastOptions.duration}ms linear forwards`
        }
      })
    ])
  , { duration: toastOptions.duration, position: toastOptions.position });
};

const instance = axios.create({
  timeout: 1000 * 60 * 10,
});

const isInApp = () =>
  !(window.location.pathname.startsWith('/embed') || window.location.pathname.startsWith('/share'));


const getErrorMessage = (response) => {
  if (response?.data?.detail && typeof response.data.detail === 'string') {
    return NOTIFICATION_MESSAGES[response.data.detail] || response.data.detail;
  }
  return isInApp()
    ? NOTIFICATION_MESSAGES.FAILED_IN_APP
    : NOTIFICATION_MESSAGES.FAILED_IN_EMBED;
};


instance.interceptors.request.use((config) => {
  const userStore = useUserStore.getState();
  const teamManagementStore = useTeamManagementStore.getState();

  // Only add Bearer token if there's no x-api-key header present
  // This prevents conflicts when demo endpoints use x-api-key authentication
  const token = userStore.auth.access_token;
  const hasApiKey = config.headers && config.headers['x-api-key'];
  if (token && isInApp() && !hasApiKey) {
    config.headers.Authorization = `Bearer ${token}`;
  }


  config.headers['x-client'] = 'dante';
  config.headers['Called-From'] = 'app.dante-ai.com';


  const selectedTeamId = teamManagementStore.selectedTeam?.id;
  if (selectedTeamId) {
    config.params = {
      ...config.params,
      team_id: selectedTeamId,
    };
  }

  return config;
});


instance.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Request failed:', error);
    console.log('error.response', error);

    if (error._handledLocally || error.config?._suppressGlobalError) {
      return Promise.reject(error);
    }

    if (error?.response?.status === 402) {
      window.open('/onboarding/upgrade?trial_ended=true', '_self');
      return;
    }

    // if (error?.response?.status === 426) {
    //   window.open('/onboarding/upgrade?old_to_new=true', '_self');
    //   return;
    // }

    if (error?.response?.status === 423) {
      window.open('/account-paused', '_self');
      return;
    }

    if (error?.response?.status === 401) {
      // Use addErrorToast to match useToast pattern
      addErrorToast({
        message: 'Your session has expired. Please log in again.'
      });

      return Promise.reject(error);
    }

    // Handle 500 Internal Server Error with custom frontend message
    if (error?.response?.status === 500 || error?.code === 'ERR_NETWORK') {
      // Use addErrorToast to match useToast pattern
      addErrorToast({
        message: NOTIFICATION_MESSAGES.SERVER_ERROR
      });

      return Promise.reject(error);
    }

    // Handle 404 Not Found errors
    if (error?.response?.status === 404) {
      const message = getErrorMessage(error.response);
      addErrorToast({
        message: message
      });

      return Promise.reject(error);
    }

    // Handle 422 Unprocessable Entity errors (validation errors)
    if (error?.response?.status === 422) {
      const message = getErrorMessage(error.response);
      addErrorToast({
        message: message
      });

      return Promise.reject(error);
    }

    // Handle 403 Forbidden errors
    if (error?.response?.status === 403) {
      const message = getErrorMessage(error.response);
      addErrorToast({
        message: message
      });

      return Promise.reject(error);
    }

    // For other errors, show backend message or fallback
    const message = getErrorMessage(error.response);

    // Use addErrorToast to match useToast pattern
    addErrorToast({
      message: message
    });

    return Promise.reject(error);
  }
);

export default instance;
